import React, { useEffect, useState } from "react";
import { logError } from "../lib/log";
import { Link } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { CalendarClock, Plus, AlertTriangle } from "lucide-react";
import { AssignmentForm, AssignmentRow, emptyAssignmentForm } from "./AssignmentParts";

export function StudentAssignments() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/me/assignments").then((r) => setItems(r.data.assignments)).catch((e) => logError("Assignments load failed", e)); }, []);
  if (items.length === 0) return null;
  return (
    <section className="rounded-md border border-white/10 border-t-2 border-t-amber-400 bg-black/40 p-6" data-testid="student-assignments">
      <div className="flex items-center gap-2">
        <CalendarClock size={16} className="text-amber-300" />
        <h2 className="font-display text-xl font-semibold text-white">Homework</h2>
        <span className="rounded-full border border-amber-400/40 bg-amber-500/10 px-2 py-0.5 text-[10px] uppercase tracking-widest text-amber-300">{items.length}</span>
      </div>
      <div className="mt-4 space-y-2">
        {items.map((a) => {
          const to = a.kind === "lesson" ? `/curriculum/${a.year}/${a.order}` : `/c/${a.quiz_id}`;
          return (
            <Link key={a.id} to={to} data-testid={`hw-${a.id}`} className={`flex items-center justify-between rounded-md border px-4 py-3 hover:border-cyan-400/40 ${
              a.completed_at ? "border-lime-400/40 bg-lime-500/5" :
              a.overdue ? "border-red-400/40 bg-red-500/5" :
              "border-white/10 bg-black/60"
            }`}>
              <div>
                <div className="text-sm font-semibold text-white">{a.title}</div>
                <div className="mt-1 flex items-center gap-3 text-[10px] uppercase tracking-widest text-slate-500">
                  <span>{a.kind === "lesson" ? `Lesson Y${a.year} · #${a.order}` : "Custom quiz"}</span>
                  <span>Due {new Date(a.due_at).toLocaleDateString()}</span>
                </div>
              </div>
              {a.completed_at ? (
                <span className="rounded-md border border-lime-400/40 bg-lime-500/10 px-2 py-1 text-[10px] text-lime-200">
                  {a.on_time ? "✓ On time" : "✓ Late"}
                </span>
              ) : a.overdue ? (
                <span className="inline-flex items-center gap-1 rounded-md border border-red-400/40 bg-red-500/10 px-2 py-1 text-[10px] text-red-200"><AlertTriangle size={10} /> Overdue</span>
              ) : (
                <span className="rounded-md border border-amber-400/40 bg-amber-500/10 px-2 py-1 text-[10px] text-amber-200">Open</span>
              )}
            </Link>
          );
        })}
      </div>
    </section>
  );
}

const toPayload = (form) => ({
  kind: form.kind,
  year: form.kind === "lesson" ? Number(form.year) : null,
  order: form.kind === "lesson" ? Number(form.order) : null,
  quiz_id: form.kind === "custom-quiz" ? form.quiz_id : null,
  title: form.title,
  due_at: new Date(form.due_at).toISOString(),
});

function useTeacherAssignments() {
  const [items, setItems] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [err, setErr] = useState(null);
  const load = () => {
    api.get("/teacher/assignments").then((r) => setItems(r.data.assignments)).catch((e) => setErr(apiError(e, "Could not load assignments")));
    api.get("/teacher/quizzes").then((r) => setQuizzes(r.data.quizzes)).catch((e) => logError("Quiz list failed", e));
  };
  useEffect(() => { load(); }, []);
  const run = async (fn, fallback) => {
    setErr(null);
    try { await fn(); load(); return true; } catch (e) { setErr(apiError(e, fallback)); return false; }
  };
  const create = (form) => run(() => api.post("/teacher/assignments", toPayload(form)), "Could not create assignment");
  const remove = (id) => run(() => api.delete(`/teacher/assignments/${id}`), "Could not delete assignment");
  return { items, quizzes, err, create, remove };
}

export function TeacherAssignments() {
  const { items, quizzes, err, create, remove } = useTeacherAssignments();
  const [showNew, setShowNew] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [form, setForm] = useState(emptyAssignmentForm);
  const onCreate = async () => {
    if (await create(form)) { setShowNew(false); setForm(emptyAssignmentForm()); }
  };

  return (
    <section className="rounded-md border border-white/10 bg-black/40 p-6" data-testid="teacher-assignments">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-amber-300">Homework</div>
          <h2 className="font-display text-xl font-semibold text-white">Assignments</h2>
        </div>
        <button onClick={() => setShowNew(!showNew)} data-testid="new-assignment-btn" className="inline-flex items-center gap-2 rounded-md border border-amber-400/40 bg-amber-500/10 px-3 py-1 text-xs text-amber-200 hover:bg-amber-500/20">
          <Plus size={12} /> {showNew ? "Cancel" : "New assignment"}
        </button>
      </div>

      {showNew && <AssignmentForm form={form} setForm={setForm} quizzes={quizzes} err={err} onCreate={onCreate} />}
      {!showNew && err && <div className="mt-3 rounded-md border border-red-400/40 bg-red-500/10 p-2 text-xs text-red-200" data-testid="assignments-error">{err}</div>}

      <div className="mt-4 space-y-2">
        {items.length === 0 && !showNew && <div className="text-center text-sm text-slate-500">No assignments yet.</div>}
        {items.map((a) => (
          <AssignmentRow key={a.id} a={a} isOpen={!!expanded[a.id]} onToggle={() => setExpanded({ ...expanded, [a.id]: !expanded[a.id] })} onRemove={remove} />
        ))}
      </div>
    </section>
  );
}
